import { useCallback, useEffect, useRef, useState } from "react";
import { PushSettingsPanel } from "./PushSettingsPanel";
import { PUSH_ACTIONS, pushPanelState } from "../pushSettings.js";
import {
  PUSH_ANSWERS, isInstalledApp, isIosDevice, pushSupported,
  subscriptionPayload, urlBase64ToUint8Array,
} from "../pushOptIn.js";
import { t } from "../strings.js";

// P4 — **THE DOOR. THE MANUAL WAY IN AND OUT OF NOTIFICATIONS, AND UNTIL THIS FILE THERE WAS NONE.**
//
// Measured before it was built (R25): `src/pushOptIn.js` and `server/push-routes.js` both said in
// their own comments that "the bell in My Room stays as the manual way in", and no such control had
// ever existed. `shouldOfferPush` refuses on ANY prior answer, so **"Not now" was permanent**, and
// `/api/push/unsubscribe` had zero client call sites (R38's trap, on the route whose whole job is
// letting her stop). Both halves are closed here.
//
// The split is the one `PushOptIn`/`PushOptInSheet` already uses: this file holds the state and the
// async steps, `PushSettingsPanel` is hookless and CALLED by the gate, and the decision itself is
// pure in `src/pushSettings.js`. **App.tsx costs ZERO lines** — the panel mounts inside
// `AccountMenuPanel`, which is where account controls already live.
const post = (url: string, body: unknown) =>
  fetch(url, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) })
    .then((r) => r.json()).catch(() => null);

export function PushSettings({ language = "en" }: { language?: string }) {
  // P4-1 — **NULL IS "NOT LOOKED YET", NEVER "OFF".** `pushPanelState(null)` answers `unknown` and
  // the panel says so, because a control that draws OFF while it is still reading tells a subscribed
  // member she is unsubscribed and invites her to repair something that is not broken.
  const [device, setDevice] = useState<null | Record<string, unknown>>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  // **READ LIVE FROM THE BROWSER AND NEVER FROM A STORED ANSWER** — P4-1's own words, and the
  // stale-grant lesson this house has paid for three times (P3-2). The account's `push_answer` is
  // deliberately not consulted: it is what she TOLD us, and this panel reports whether this device
  // actually receives. It touches NO route, so opening the account menu costs nothing.
  const readDevice = useCallback(async () => {
    const supported = pushSupported(window);
    const installed = isInstalledApp(window);
    const iosDevice = isIosDevice(window);
    const permission = window.Notification?.permission || "default";
    let subscribed = false;
    if (supported) {
      try {
        const reg = await navigator.serviceWorker.ready;
        subscribed = Boolean(await reg.pushManager.getSubscription());
      } catch { subscribed = false; }
    }
    if (alive.current) setDevice({ supported, installed, iosDevice, permission, subscribed });
  }, []);

  useEffect(() => { void readDevice(); }, [readDevice]);

  // ---------------------------------------------------------------------------
  // P4-2 — ONE TAP EACH WAY.
  //
  // **NOTHING IS AWAITED ABOVE `requestPermission`.** It is gesture-gated, and an await before it can
  // cost the gesture on some browsers — which is why the VAPID key is fetched AFTER the permission
  // resolves rather than held in a ref: `pushManager.subscribe` needs the PERMISSION, not the
  // gesture, so the round trip is free to happen there and the account menu opens with no request.
  // ---------------------------------------------------------------------------
  const subscribe = async () => {
    let permission = "default";
    try { permission = await window.Notification.requestPermission(); } catch { permission = "default"; }
    if (permission !== "granted") {
      // **A DISMISSAL RECORDS NOTHING, AND THAT IS A DELIBERATE DIVERGENCE FROM THE SHEET.** The
      // sheet writes `later` on a dismissal because the sheet is a one-shot and the record is what
      // stops it nagging. This panel is permanent and she is standing in front of it, so nothing has
      // changed and nothing should be written. A denial IS recorded, because it is an answer.
      if (permission === "denied") await post("/api/push/answer", { answer: PUSH_ANSWERS.DENIED });
      await readDevice();
      return;
    }
    const key = await fetch("/api/push/key").then((x) => x.json()).catch(() => null);
    if (!key?.ok || !key.configured || !key.key) { setNote(t(language, "push.failed")); return; }
    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    const sub = existing || await reg.pushManager.subscribe({
      // Required by every browser: a push that shows nothing is not allowed, and asking for one is
      // how a subscription gets refused outright.
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key.key),
    });
    const payload = subscriptionPayload(sub);
    if (!payload) { setNote(t(language, "push.failed")); return; }
    // R39/R47/R53: the ANSWER is read, never the status code, and this route answers refusals with a
    // payload. The server records the grant beside the row, so the two cannot disagree — and OR-4's
    // welcome fires from there, once per endpoint, which is what makes a re-opt-in prove the chain
    // works again on this device.
    const saved = await post("/api/push/subscribe", payload);
    if (!saved?.ok) setNote(t(language, "push.failed"));
  };

  // **THE ORDER OF THE THREE WRITES IS THE LOAD-BEARING PART OF THIS FILE, AND IT IS HELD BY
  // POSITION** (R52/R138). `healDecision` answers RESUBSCRIBE for an account saying `granted` with
  // no live subscription and the permission still granted — **exactly the state an opt-out leaves
  // behind** — and it runs on every launch of the installed app, prompt-free. So:
  //
  //   · **THE ANSWER GOES FIRST.** If it lands and the sweep then fails, the account says `off`, the
  //     row survives, she keeps receiving, the panel keeps saying ON and she taps again: visible and
  //     recoverable. If the sweep landed first and the answer failed, the row is gone, the panel says
  //     OFF, and the heal silently re-mints on her next launch — **her own choice reversed with
  //     nothing on screen**, which is the shape this whole system exists to stop.
  //   · **THE BROWSER IS UNSUBSCRIBED BEFORE THE SERVER ROW IS SWEPT.** The reverse leaves a live
  //     browser subscription with no row behind it, which is silent decay. This way round the worst
  //     case is an orphaned row whose endpoint is dead, and the push service's own 404/410 makes
  //     `recordSendResult` delete it (`server/db/push.js`'s own law).
  //   · **THIS DEVICE, NOT THE ACCOUNT.** The panel's whole sentence is about this device, and a
  //     control that reads "this device" and acts on every device she owns is a control that lies.
  //     The route takes an endpoint for exactly this reason.
  const unsubscribe = async () => {
    const answered = await post("/api/push/answer", { answer: PUSH_ANSWERS.OFF });
    if (!answered?.ok) { setNote(t(language, "push.failed")); return; }
    let endpoint = "";
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { endpoint = String(sub.endpoint || ""); await sub.unsubscribe(); }
    } catch { setNote(t(language, "push.failed")); return; }
    const swept = await post("/api/push/unsubscribe", { endpoint });
    if (!swept?.ok) setNote(t(language, "push.failed"));
  };

  const act = async (action: string) => {
    if (busy) return;
    setBusy(true);
    setNote("");
    try {
      if (action === PUSH_ACTIONS.SUBSCRIBE) await subscribe();
      else if (action === PUSH_ACTIONS.UNSUBSCRIBE) await unsubscribe();
    } catch {
      setNote(t(language, "push.failed"));
    } finally {
      // **THE PANEL RE-READS THE BROWSER RATHER THAN PAINTING WHAT IT HOPED FOR** (R54's ruling
      // inverted: there is no optimistic paint to put back, because the authority is right there and
      // costs nothing to ask). The put-back is in a `finally` so a thrown act cannot leave a
      // permanently dead control with her choice half-made.
      await readDevice();
      if (alive.current) setBusy(false);
    }
  };

  // **IT ALWAYS RENDERS, INCLUDING ON A DEVICE THAT CAN NEVER DO THIS** (P4-4: an unsupported
  // context says so honestly rather than offering a moment that cannot complete). Hiding the panel
  // where push is impossible would make "your browser cannot" and "this house has no such setting"
  // the same screen, which is R18's distinction on the control rather than on the state. It is
  // `PushSettingsPanel` that decides there is no BUTTON, from the state alone.
  return <PushSettingsPanel state={pushPanelState(device)} busy={busy} note={note} language={language} onAct={act} />;
}
